import { useEffect, useState } from "react"

export type HoverElement<T, E extends HTMLElement = HTMLElement> = {
    ref: React.RefObject<E | null>;
    value: T;
};

export function useHoverMonitor<T, E extends HTMLElement = HTMLElement>(
    hoverElements: HoverElement<T, E>[],
): {
    set: Set<T> // a set of all values being hovered simultaneously (in case of overlap)
    unique: T | null // returns a value being hovered uniquely (if 0 or > 1 values hovered, returns null)
}{
    const [hoverSet, setHoverSet] = useState<Set<T>>(new Set());
    const [hoverUnique , setHoverUnique] = useState<T | null>(null);

    useEffect(() => {
        if (hoverElements.length < 1)
            return;

        const elementMap: Map<E, T> = new Map();
        for (const hoverElement of hoverElements) {
            if (!hoverElement.ref.current) {
                continue;
            }
            elementMap.set(hoverElement.ref.current, hoverElement.value);
        }

        function handleMouseEnter(e: MouseEvent) {
            const el = e.currentTarget as E | null;
            if (!el) {
                return;
            }
            const value = elementMap.get(el);
            if (!value) {
                return;
            }
            setHoverSet(prev => {
                // only set to unique if the set was previously empty
                const next = new Set(prev);
                next.add(value);
                return next;
            });
        }
        function handleMouseLeave(e: MouseEvent) {
            const el = e.currentTarget as E | null;
            if (!el) {
                return;
            }
            const value = elementMap.get(el);
            if (!value) {
                return;
            }
            setHoverSet(prev => {
                const next = new Set(prev);
                next.delete(value);
                return next;
            });
        }
        function register(el: E) {
            el.addEventListener('mouseenter', handleMouseEnter);
            el.addEventListener('mouseleave', handleMouseLeave);
        }
        function unregister(el: E) {
            el.removeEventListener('mouseenter', handleMouseEnter);
            el.removeEventListener('mouseleave', handleMouseLeave);
        }

        for (const hoverElement of hoverElements) {
            const element = hoverElement.ref.current;
            if (!element) {
                continue;
            }
            register(element);
        }
        return () => {
            for (const hoverElement of hoverElements) {
                const element = hoverElement.ref.current;
                if (!element) {
                    continue;
                }
                unregister(element)
            }
        }
    }, [hoverElements]);

    // sync hoverUnique to hoverSet
    useEffect(() => {
        if (hoverSet.size === 1) {
            const value: T | undefined = hoverSet.values().next().value;
                if (value) {
                    setHoverUnique(value)
                }  
        } else {
            setHoverUnique(null);
        }
    }, [hoverSet])

    return {
        set: hoverSet,
        unique: hoverUnique
    }
}